<script>
    import { params, redirect, page } from "@roxi/routify";
    import { formattedDate, formattedDateAndTime } from "$/utils/date";
    import { exportTopicToCsv } from "$/utils/CsvExporter";
    import { clickAction } from "$/utils/clickAction";
    import SVGIcon from "$/components/SVGIcon.svelte";
    import SharePreferencesPanel from "$/components/SharePreferencesPanel.svelte";
    import SidePanel from "$/components/SidePanel.svelte";
    import ActivitiesPanel from "$/components/activities/ActivitiesPanel.svelte";
    import BottomPopUpModal from "$/components/modals/BottomPopUpModal.svelte";
    import { TokenStore } from "$/stores/TokenStore";
    import { TenantStore } from "$/stores/TenantStore";
    import {
        ActiveTopicStore,
        ActiveTopic,
        TopicAccessLevel,
    } from "$/stores/extension/ActiveTopicStore";
    import {
        ClusterStore,
        ClusterType,
    } from "$/stores/ClusterStore.ts";
    import { TopicViewStore, TopicViewTypes } from "$/stores/TopicViewStore";
    import { NotificationService } from "$/services/NotificationService";
    import { tooltip } from "$/utils/tooltip";
    import { clickOutside } from "$/utils/clickOutside";
    import { SelectedSnippetsStore } from "$/stores/SelectedSnippetsStore";
    import { BottomPopUpModalAction } from "$/components/modals/BottomPopUpModalAction.js";
    import DashboardViewManager from "$/components/DashboardViewManager/DashboardViewManager.svelte";
    import { AppStore, Panels } from "$/stores/AppStore";
    import { RecentTopicsStore } from "$/stores/RecentTopicsStore";
    import CopyTopicDropdown from "$/components/dropdowns/copyTopicDropdown/CopyTopicDropdown.svelte";
    import IntroModal from "$/components/modals/IntroModal/IntroModal.svelte";
    import { TopicService } from "$/services/TopicService";
    import { ServiceConsts } from "$/services/ServiceConsts";
    import {
        getSnippetUrlPreview,
        getEffectivePricingPlan,
        getRole,
        copyToClip,
        getFullName,
        PAGE_WRAPPER_ID,
        showtToastMessage,
        clearUrlByMarker,
        checkIfExtensionInstalled,
        getExtensionUrl, canUserEditTeamCluster,
    } from "$/utils/Utils";
    import { onDestroy, onMount } from "svelte";
    import { GlobalSearchStore } from "$/stores/GlobalSearchStore";
    import AddLinkDropdown from "$/components/dropdowns/AddLinkDropdown.svelte";
    import ArchivedSnippetsPanel from "$/components/ArchivedSnippetsPanel.svelte";

    let oldParamTopicId;

    let notificationsSubscriptionStatus = false;

    let showDeleteModal = false;
    let showLeaveModal = false;
    let showMoreOptionsMenu = false;
    let showMoreOptionsMenuButton;
    let showArchiveModal = false;
    let showCopyToOptions = false;
    let showQuickAddMenu = false;

    let isExtensionInstalled = true;

    let placeholderTopic;
    let topicNameInput;
    let placeholderTopicName = "Loading topic";
    let placeholderTopicDate = "";
    let placeholderTopicArchived = false;
    let selectedTagNames = [];

    let addLink = false;

    $: selectedTagNames;

    $: $params, loadTopic();

    async function loadTopic() {
        if ($params.topicId !== oldParamTopicId) {
            AppStore.closePanels();
            // TODO: If topic id in cache then initialize tenants and clusters from cache
            //       because that means it's the right tenant, otherwise skip
            if ($TenantStore === null) await TenantStore.init();
            if ($ClusterStore?.clusters === null)
                await ClusterStore.init($TenantStore.active.id);

            // set the old topic id
            oldParamTopicId = $params.topicId;

            // set placeholders
            placeholderTopic = ClusterStore.findTopic($params.topicId);

            if (placeholderTopic) {
                placeholderTopicName = placeholderTopic.name;
                placeholderTopicDate = formattedDate(
                    placeholderTopic?.createdOn,
                );
                placeholderTopicArchived = placeholderTopic.archived;
            }

            // if cursor is in input field, value won't change so we need to set it manually
            if (topicNameInput) topicNameInput.value = placeholderTopicName;

            let topic = null;

            // try to load topic if theres error redirect to dashboard
            // now also tries to load clusters and then if theres an error it will redirect to dashboard
            // TODO: maybe we can in future add 404 page on topic not found
            try {
                topic = await ActiveTopicStore.init(
                    $params.topicId,
                    TokenStore,
                    TenantStore,
                    ClusterStore,
                );
            } catch (e) {
                console.error(e);
                $redirect("/");
            }

            // if topic is not the part of the current tenant reload tenant and cluster stores
            if (
                $TenantStore === null ||
                (topic?.tenantId !== $TenantStore.active.id &&
                    topic?.createdById === $TokenStore.user.id)
            ) {
                await TenantStore.load(topic.tenantId);
                await ClusterStore.init($TenantStore.active.id);
            }

            const topicUserRole = getRole(
                $ActiveTopic,
                $TokenStore.user?.id,
                $TenantStore?.tenants,
                $ClusterStore.clusters,
            );

            if (topicUserRole >= TopicAccessLevel.COMMENTER) {
                await ActiveTopicStore.fetchCollaborators();
            }

            if ($params.id && $params.type === "COMMENT") {
                let comments = topic.snippets
                    .map((snippet) => snippet.comments)
                    .flat();
                const commentIndex = comments.findIndex(
                    (comment) => comment.id === $params.id,
                );

                if (commentIndex < 0)
                    showtToastMessage("Comment couldn't be found");
            }

            if ($params.id && $params.type === "SNIPPET") {
                let snippetIndex = topic.snippets.findIndex(
                    (snippet) => snippet.id === $params.id,
                );
                if (snippetIndex < 0)
                    showtToastMessage("Snippet couldn't be found");
            }

            if ($RecentTopicsStore !== null) {
                const sharedClusters = $ClusterStore.clusters.filter(
                    (cluster) =>
                        cluster.clusterType === ClusterType.SHARED_WITH_ME ||
                        cluster.clusterType === ClusterType.SHARED,
                );
                const sharedTopics = sharedClusters
                    .map((cluster) => cluster.topics)
                    .flat();
                if (
                    $ActiveTopic &&
                    sharedTopics.find(
                        (sharedTopic) => sharedTopic.id === $ActiveTopic.id,
                    ) === undefined
                )
                    RecentTopicsStore.addMostRecent($ActiveTopic);
            }
        }
    }

    $: topicCreated = formattedDate($ActiveTopic?.createdOn);
    $: userIsTenantMember =
        $ActiveTopic && $TenantStore
            ? $TenantStore.tenants.some((t) => t.id === $ActiveTopic.tenant?.id)
            : false;
    $: userHasRole = $ActiveTopic
        ? $ActiveTopic.roles.some((r) => r.user.id === $TokenStore.user.id)
        : false;
    $: userHasClusterRole =
        $ClusterStore.clusters && $ActiveTopic
            ? ClusterStore.getTopicRoles($ActiveTopic.id, $TokenStore.user.id)
                  .length > 0
            : false;
    $: userTopicRole = $ActiveTopic
        ? getRole(
              $ActiveTopic,
              $TokenStore.user.id,
              $TenantStore?.tenants,
              $ClusterStore.clusters,
          )
        : TopicAccessLevel.NO_ACCESS;
    $: topicFromSharedCluster =
        $ClusterStore.clusters && $ActiveTopic
            ? $ClusterStore.clusters
                  .find(
                      (cluster) =>
                          cluster.clusterType === ClusterType.SHARED_WITH_ME,
                  )
                  ?.topics.some((topic) => topic.id === $ActiveTopic.id)
            : false;
    $: effectivePricingPlan = getEffectivePricingPlan($ActiveTopic?.tenant);
    $: archivableTopics =
        $ClusterStore.clusters &&
        $ClusterStore.clusters
            .filter((cluster) => {
                return (
                    [
                        ClusterType.ARCHIVED,
                        ClusterType.TENANT_DEFAULT,
                        ClusterType.TENANT,
                    ].includes(cluster.clusterType) &&
                    (TenantStore.isPersonal() ||
                        (!TenantStore.isPersonal() &&
                            canUserEditTeamCluster($TokenStore?.user?.id, cluster)))
                );
            })
            .map((el) => el.topics)
            .flat();

    $: userCanArchive =
        $ActiveTopic &&
        effectivePricingPlan !== -1 &&
        archivableTopics &&
        archivableTopics.some((topic) => topic.id === $ActiveTopic.id);

    $: createdBy = getFullName(
        ActiveTopicStore.getOwnerRole($ActiveTopic)?.user,
    );

    $: {
        // add visitor role if necessary
        if (
            $ActiveTopic &&
            $ClusterStore.clusters &&
            $ActiveTopic.publicRole > 0 &&
            userHasRole === false && // needs to be false because when loading it's undefined
            userHasClusterRole === false &&
            userIsTenantMember === false
        ) {
            ActiveTopicStore.addVisitorRole();
            ClusterStore.addTopicToCluster(
                $ActiveTopic,
                ClusterType.SHARED_WITH_ME,
            );
        }
    }

    $: {
        if ($params.topicId) {
            loadNotificationSubscriptionStatus($params.topicId);
        }
    }

    $: if ($params.id) {
        //Highlight comment or snippet
        if ($params.type === "COMMENT") {
            AppStore.setFocused({ commentId: $params.id });
        } else {
            if (
                $params.archived === 'true' &&
                $AppStore.panel !== Panels.SNIPPET_ARCHIVE
            ) {
                AppStore.setPanel(Panels.SNIPPET_ARCHIVE);
            }
            AppStore.setFocused({ snippetId: $params.id });
        }

        clearUrlByMarker("?");
    }

    async function loadNotificationSubscriptionStatus(topicId) {
        notificationsSubscriptionStatus =
            await NotificationService.getTopicNotificationSubscriptionStatus(
                topicId,
            );
    }

    function handleTopicNameEdited(event) {
        let topicNameEdited = event.target.value;
        if (topicNameEdited.trim() === "") {
            topicNameEdited = "Untitled Topic";
            event.target.value = topicNameEdited;
        }
        let topicFieldsToUpdate = {
            id: $ActiveTopic.id,
            name: topicNameEdited,
        };

        ClusterStore.updateTopic(topicFieldsToUpdate);
        ActiveTopicStore.updateTopic(topicFieldsToUpdate);
        RecentTopicsStore.updateTopic({
            ...$ActiveTopic,
            name: topicNameEdited,
        });
    }

    async function handleTopicDeleteClicked() {
        const topicId = $ActiveTopic.id;
        ClusterStore.removeTopicFromAllClusters(topicId);
        RecentTopicsStore.deleteTopic(topicId);
        $redirect("/");
        await TopicService.deleteTopic(topicId);
        TenantStore.refreshCollaboratorsList();
    }

    async function handleTopicArchiveClicked() {
        ActiveTopicStore.setArchived(true);
        showArchiveModal = false;
        await ClusterStore.archiveTopic($ActiveTopic.id, true);
    }

    async function unarchiveTopic() {
        ActiveTopicStore.setArchived(false);
        await ClusterStore.unarchiveTopicToBottomOfList($ActiveTopic);
    }

    async function handleTopicLeaveClicked() {
        let userRole = $ActiveTopic.roles.find(
            (r) => r.user.id === $TokenStore.user.id,
        );

        ClusterStore.leaveTopic($ActiveTopic.id, userRole.id);
        $redirect("/");
    }

    function openPrintDialog() {
        window.open(
            `/print/${$ActiveTopic.id}?openPrintDialog=true&closeAfterPrint=true&title=${$ActiveTopic.name}`,
            "_blank",
            "noopener",
        );
    }

    function subscribeTopicNotifications() {
        notificationsSubscriptionStatus = true;

        NotificationService.subscribeTopicNotifications($ActiveTopic.id);
    }

    function unsubscribeTopicNotifications() {
        notificationsSubscriptionStatus = false;

        NotificationService.unsubscribeTopicNotifications($ActiveTopic.id);
    }

    function addTagToFilter(tag) {
        selectedTagNames = [...selectedTagNames, tag.tag];
    }

    function removeTagFromFilter(tag) {
        selectedTagNames = [
            ...selectedTagNames.filter((name) => name !== tag.tag),
        ];
    }

    function generateTopicMarkdown() {
        let markdown = "";
        markdown = `<h1>${$ActiveTopic.name}</h1>`;
        $ActiveTopic.snippets.forEach((snippet, index) => {
            if (index > 0) {
                markdown += `<hr/>`;
            }
            markdown += `<h3>Snippet - <a href="${snippet.url}">[${getSnippetUrlPreview(snippet.url)}]</a></h3>`;
            
            if (snippet.name) {
                markdown += `<h4>${snippet.name}</h4>`;
            }
            
            markdown += `<mark class="highlight-pink_background">${snippet.content}</mark>`;
            if (snippet.comments && snippet.comments.length > 0) {
                markdown += `<h4>Comments</h4>`;
                if (snippet.comments) {
                    markdown += `<ul>`;
                    snippet.comments.forEach((comment) => {
                        markdown += `<li><em><strong>${comment.createdBy?.firstName} ${comment.createdBy?.lastName}</strong>, ${formattedDateAndTime(comment.createdOn)}</em> <blockquote>${comment.comment}</blockquote></li>`;
                    });
                    markdown += `</ul>`;
                }
            }
        });
        copyToClip(markdown);

        showtToastMessage("Topic copied to clipboard");
    }

    // start of csv export
    function downloadTopicAsCSV() {
        exportTopicToCsv($ActiveTopic, $ActiveTopic.name.trim());
    }

    // end of csv export

    // start of selected snippets count texts
    let initialText;
    let deleteConfirmationText;
    let deleteModalAction = new BottomPopUpModalAction(
        async () => {
            await deleteModalAction.deleteSelectedSnippets(
                $SelectedSnippetsStore,
            );
        },
        async () => {
            SelectedSnippetsStore.clear();
        },
        "Delete",
        "Deleting snippets...",
        "pink",
    );

    $: {
        initialText = `<strong>${$SelectedSnippetsStore.length} ${$SelectedSnippetsStore.length > 1 ? " snippets" : " snippet"}</strong> selected`;
        deleteConfirmationText =
            `Delete <strong>${$SelectedSnippetsStore.length}` +
            `${$SelectedSnippetsStore.length > 1 ? " snippets" : " snippet"}</strong>?`;
    }

    // end of selected snippets count texts

    function setView(id, view) {
        let currentData = $TopicViewStore;

        const index = currentData.findIndex((item) => item.id === id);

        if (index !== -1) {
            currentData[index].view = currentData[index].view = view;
        } else {
            currentData.push({ id, view: view });
        }

        TopicViewStore.set(currentData);
    }

    $: selectedView = $TopicViewStore
        ? TopicViewStore.selectedViewByTopicId($ActiveTopic?.id)
        : TopicViewTypes.BOARD;

    onMount(async () => {
        isExtensionInstalled = await checkIfExtensionInstalled();
    });
</script>

{#key $page.path}
    <IntroModal />

    {#if showDeleteModal}
        <div class="delete-topic-modal-wrapper">
            <div
                class="delete-topic-modal"
                use:clickOutside
                on:click_outside={() => (showDeleteModal = false)}
            >
                <h1>Are you sure you want to delete this topic?</h1>
                <p>
                    This action cannot be undone, and all snippets in this topic
                    will be deleted.
                </p>
                <div class="delete-topic-modal__actions">
                    <button
                        class="btn btn--secondary"
                        on:click={() => {
                            showDeleteModal = false;
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        class="btn btn--danger"
                        on:click={handleTopicDeleteClicked}
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    {/if}

    {#if showArchiveModal}
        <div class="delete-topic-modal-wrapper">
            <div
                class="delete-topic-modal"
                use:clickOutside
                on:click_outside={() => (showArchiveModal = false)}
            >
                <h1>Are you sure you want to archive this topic?</h1>
                <div class="delete-topic-modal__actions">
                    <button
                        class="btn btn--secondary"
                        on:click={() => {
                            showArchiveModal = false;
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        class="btn btn--danger"
                        on:click={handleTopicArchiveClicked}
                    >
                        Archive
                    </button>
                </div>
            </div>
        </div>
    {/if}

    {#if $SelectedSnippetsStore.length > 0}
        <BottomPopUpModal
            actions={[deleteModalAction]}
            {initialText}
            confirmationTexts={[deleteConfirmationText]}
        />
    {/if}

    {#if showLeaveModal}
        <div class="delete-topic-modal-wrapper">
            <div
                class="delete-topic-modal"
                use:clickOutside
                on:click_outside={() => (showLeaveModal = false)}
            >
                <h1>Are you sure you want to leave this topic?</h1>
                <p>
                    This action cannot be undone. You will have to request
                    membership from topic owner.
                </p>
                <div class="delete-topic-modal__actions">
                    <button
                        class="btn btn--secondary"
                        on:click={() => {
                            showLeaveModal = false;
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        class="btn btn--danger"
                        on:click={handleTopicLeaveClicked}
                    >
                        Leave
                    </button>
                </div>
            </div>
        </div>
    {/if}

    <div id={PAGE_WRAPPER_ID} class="topic-page-wrapper">
        <div class="topic-header">
            <!-- Topic Header -->
            <div class="topic-header__info">
                <input
                    class="topic-header__info-input"
                    bind:this={topicNameInput}
                    type="text"
                    value={$ActiveTopic !== null
                        ? $ActiveTopic.name
                        : placeholderTopicName}
                    on:change={handleTopicNameEdited}
                    disabled={userTopicRole < TopicAccessLevel.EDITOR}
                />

                <div class="topic-header__info-footer">
                    {#if $ActiveTopic !== null ? $ActiveTopic.archived : placeholderTopicArchived}
                        <span class="topic-label">Archived</span>
                    {/if}
                    <span
                        >{$ActiveTopic !== null
                            ? topicCreated
                            : placeholderTopicDate}</span
                    >

                    {#if createdBy}
                        <span>-</span>
                        <span>{createdBy}</span>
                    {/if}
                </div>
            </div>

            <!-- Topic Actions -->
            {#if $ActiveTopic}
                <div class="topic-header__option-wrapper">
                    {#if userTopicRole >= TopicAccessLevel.EDITOR}
                        <div class="relative">
                            <button
                                class="topic-header__option-button mr-1"
                                use:tooltip={"Add Snippets"}
                                on:click={() =>
                                    (showQuickAddMenu = !showQuickAddMenu)}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    class="size-6"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 9a.75.75 0 0 0-1.5 0v2.25H9a.75.75 0 0 0 0 1.5h2.25V15a.75.75 0 0 0 1.5 0v-2.25H15a.75.75 0 0 0 0-1.5h-2.25V9Z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </button>

                            {#if showQuickAddMenu}
                                <div
                                    use:clickOutside
                                    on:click_outside={(event) => {
                                        showQuickAddMenu = false;
                                    }}
                                >
                                    <div class="topic-header__dropdown">
                                        <div
                                            class="topic-header__dropdown-options-wrapper"
                                            role="menu"
                                            aria-orientation="vertical"
                                            aria-labelledby="quick-add-menu"
                                        >
                                            <div
                                                class="topic-header__dropdown-option"
                                                use:clickAction={() => {
                                                    showQuickAddMenu = false;
                                                    addLink = true;
                                                }}
                                            >
                                                <div
                                                    class="topic-header__option-button"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke-width="1.5"
                                                        stroke="currentColor"
                                                        class="size-6"
                                                    >
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
                                                        />
                                                    </svg>
                                                </div>
                                                <div>Add Link</div>
                                            </div>

                                            <a
                                                href={`/pdf`}
                                                class="topic-header__dropdown-option"
                                            >
                                                <div
                                                    class="topic-header__option-button"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke-width="1.5"
                                                        stroke="currentColor"
                                                        class="size-6"
                                                    >
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                                                        />
                                                    </svg>
                                                </div>
                                                <div>Open PDF</div>
                                            </a>
                                            <a
                                                href={`https://read.amazon.com/notebook#${ServiceConsts.TOPIC_PARAM_NAME}=${$ActiveTopic.id}`}
                                                target="_blank"
                                                class="topic-header__dropdown-option"
                                                class:topic-header__dropdown-option--disabled={!isExtensionInstalled}
                                                use:clickAction={() => {
                                                    showQuickAddMenu = false;
                                                }}
                                            >
                                                <div
                                                    class="topic-header__option-button"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke-width="1.5"
                                                        stroke="currentColor"
                                                        class="size-6"
                                                    >
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            d="M10.5 19.5h3m-6.75 2.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-15a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 4.5v15a2.25 2.25 0 0 0 2.25 2.25Z"
                                                        />
                                                    </svg>
                                                </div>
                                                <div>Import from Kindle</div>
                                            </a>
                                            <div
                                                class="topic-header__dropdown-option"
                                                class:topic-header__dropdown-option--disabled={!isExtensionInstalled}
                                                use:clickAction={() => {
                                                    showQuickAddMenu = false;
                                                    GlobalSearchStore.openSearchModal(
                                                        true,
                                                    );
                                                }}
                                            >
                                                <div
                                                    class="topic-header__option-button"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke-width="1.5"
                                                        stroke="currentColor"
                                                        class="size-6"
                                                    >
                                                        <path
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                            d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
                                                        />
                                                    </svg>
                                                </div>
                                                <div>Search the Web</div>
                                            </div>

                                            {#if !isExtensionInstalled}
                                                <a
                                                    href={getExtensionUrl()}
                                                    target="_blank"
                                                    class="topic-header__dropdown-option topic-header__dropdown-option--install-extension"
                                                >
                                                    <div
                                                        class="topic-header__option-button"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke-width="1.5"
                                                            stroke="currentColor"
                                                            class="size-6"
                                                        >
                                                            <path
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 0 1-.657.643 48.39 48.39 0 0 1-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 0 1-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 0 0-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 0 1-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 0 0 .657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 0 1-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 0 0 5.427-.63 48.05 48.05 0 0 0 .582-4.717.532.532 0 0 0-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 0 0 .658-.663 48.422 48.422 0 0 0-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 0 1-.61-.58v0Z"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div>Install Extension</div>
                                                </a>
                                            {/if}
                                        </div>
                                    </div>
                                </div>
                            {/if}

                            <AddLinkDropdown bind:addLink />
                        </div>
                    {/if}

                    <div class="hidden md:block">
                        <!-- show only on large screens -->
                        {#if selectedView === TopicViewTypes.GRID}
                            <button
                                class="topic-header__option-button"
                                use:tooltip={"Board view"}
                                on:click={() =>
                                    setView(
                                        $ActiveTopic.id,
                                        TopicViewTypes.BOARD,
                                    )}
                            >
                                <SVGIcon name="columns" size="20" />
                            </button>
                        {:else}
                            <button
                                class="topic-header__option-button"
                                use:tooltip={"Grid view"}
                                on:click={() =>
                                    setView(
                                        $ActiveTopic.id,
                                        TopicViewTypes.GRID,
                                    )}
                            >
                                <SVGIcon name="board" size="20" />
                            </button>
                        {/if}
                        {#if notificationsSubscriptionStatus}
                            <button
                                class="topic-header__option-button"
                                use:tooltip={"Unsubscribe"}
                                on:click={unsubscribeTopicNotifications}
                            >
                                <SVGIcon name="bell" size="20" />
                            </button>
                        {:else}
                            <button
                                class="topic-header__option-button"
                                use:tooltip={"Subscribe"}
                                on:click={subscribeTopicNotifications}
                            >
                                <SVGIcon name="bell_crossed" size="20" />
                            </button>
                        {/if}

                        <button
                            class="topic-header__option-button"
                            use:tooltip={"Share"}
                            on:click={() => AppStore.togglePanel(Panels.SHARE)}
                        >
                            <SVGIcon name="share" size="20" />
                        </button>
                    </div>

                    <!-- Drop-down menu for remaining buttons -->
                    <div class="relative">
                        <button
                            bind:this={showMoreOptionsMenuButton}
                            class="topic-header__option-button"
                            class:bg-gray-100={showMoreOptionsMenu}
                            class:rounded-b-none={showMoreOptionsMenu}
                            use:tooltip={"More options"}
                            on:click={() => {
                                showMoreOptionsMenu = !showMoreOptionsMenu;
                                showCopyToOptions = false;
                                showQuickAddMenu = false;
                            }}
                        >
                            <SVGIcon name="ellipsis-horizontal" size="20" />
                        </button>

                        {#if showMoreOptionsMenu}
                            {#if showCopyToOptions}
                                <div
                                    use:clickOutside
                                    on:click_outside={(event) => {
                                        if (
                                            showMoreOptionsMenuButton !==
                                                event.detail.target &&
                                            !showMoreOptionsMenuButton.contains(
                                                event.detail.target,
                                            )
                                        ) {
                                            showMoreOptionsMenu = false;
                                            showCopyToOptions = false;
                                            showQuickAddMenu = false;
                                        }
                                    }}
                                >
                                    <CopyTopicDropdown
                                        bind:showMoreOptionsMenu
                                    />
                                </div>
                            {:else}
                                <div
                                    use:clickOutside
                                    on:click_outside={(event) => {
                                        if (
                                            showMoreOptionsMenuButton !==
                                                event.detail.target &&
                                            !showMoreOptionsMenuButton.contains(
                                                event.detail.target,
                                            )
                                        ) {
                                            showMoreOptionsMenu = false;
                                        }
                                    }}
                                    class="topic-header__dropdown"
                                >
                                    <div
                                        class="topic-header__dropdown-options-wrapper"
                                        role="menu"
                                        aria-orientation="vertical"
                                        aria-labelledby="options-menu"
                                    >
                                        <!-- show only on small screens -->
                                        <div class="block md:hidden">
                                            {#if notificationsSubscriptionStatus}
                                                <div
                                                    class="topic-header__dropdown-option"
                                                    use:clickAction={() => {
                                                        unsubscribeTopicNotifications();
                                                        showMoreOptionsMenu = false;
                                                    }}
                                                >
                                                    <div
                                                        class="topic-header__option-button"
                                                    >
                                                        <SVGIcon
                                                            name="bell"
                                                            size="20"
                                                        />
                                                    </div>
                                                    <div>Unsubscribe</div>
                                                </div>
                                            {:else}
                                                <div
                                                    class="topic-header__dropdown-option"
                                                    use:clickAction={() => {
                                                        subscribeTopicNotifications();
                                                        showMoreOptionsMenu = false;
                                                    }}
                                                >
                                                    <div
                                                        class="topic-header__option-button"
                                                    >
                                                        <SVGIcon
                                                            name="bell_crossed"
                                                            size="20"
                                                        />
                                                    </div>
                                                    <div>Subscribe</div>
                                                </div>
                                            {/if}

                                            <div
                                                class="topic-header__dropdown-option"
                                                use:clickAction={() => {
                                                    AppStore.togglePanel(
                                                        Panels.SHARE,
                                                    );
                                                    showMoreOptionsMenu = false;
                                                }}
                                            >
                                                <div
                                                    class="topic-header__option-button"
                                                >
                                                    <SVGIcon
                                                        name="share"
                                                        size="20"
                                                    />
                                                </div>
                                                <div>Share</div>
                                            </div>
                                        </div>

                                        <div
                                            class="topic-header__dropdown-option"
                                            use:clickAction={() => {
                                                showCopyToOptions = true;
                                            }}
                                        >
                                            <div
                                                class="topic-header__option-button"
                                            >
                                                <SVGIcon
                                                    name="document-duplicate"
                                                    size="20"
                                                />
                                            </div>
                                            <div>Copy to</div>
                                        </div>

                                        {#if userCanArchive && !$ActiveTopic.archived}
                                            <div
                                                class="topic-header__dropdown-option"
                                                use:clickAction={() => {
                                                    showArchiveModal = true;
                                                    showMoreOptionsMenu = false;
                                                }}
                                            >
                                                <div
                                                    class="topic-header__option-button"
                                                >
                                                    <SVGIcon
                                                        name="archive"
                                                        size="20"
                                                    />
                                                </div>
                                                <div>Archive</div>
                                            </div>
                                        {:else if userCanArchive && $ActiveTopic.archived}
                                            <div
                                                class="topic-header__dropdown-option"
                                                use:clickAction={() => {
                                                    unarchiveTopic();
                                                    showMoreOptionsMenu = false;
                                                }}
                                            >
                                                <div
                                                    class="topic-header__option-button"
                                                >
                                                    <SVGIcon
                                                        name="unarchive"
                                                        size="20"
                                                    />
                                                </div>
                                                <div>Unarchive</div>
                                            </div>
                                        {/if}

                                        <!-- show always -->
                                        {#if userHasRole}
                                            {#if userTopicRole === TopicAccessLevel.OWNER && $ActiveTopic.archived}
                                                <div
                                                    class="topic-header__dropdown-option"
                                                    use:clickAction={() => {
                                                        showDeleteModal = true;
                                                        showMoreOptionsMenu = false;
                                                    }}
                                                >
                                                    <div
                                                        class="topic-header__option-button"
                                                    >
                                                        <SVGIcon
                                                            name="delete"
                                                            size="20"
                                                        />
                                                    </div>
                                                    <div>Delete</div>
                                                </div>
                                            {:else if !userIsTenantMember && topicFromSharedCluster}
                                                <div
                                                    class="topic-header__dropdown-option"
                                                    use:clickAction={() => {
                                                        showLeaveModal = true;
                                                        showMoreOptionsMenu = false;
                                                    }}
                                                >
                                                    <div
                                                        class="topic-header__option-button"
                                                    >
                                                        <SVGIcon
                                                            name="receipt-refund"
                                                            size="20"
                                                        />
                                                    </div>
                                                    <div>Leave</div>
                                                </div>
                                            {/if}
                                        {/if}

                                        <div
                                            class="topic-header__dropdown-option"
                                            use:clickAction={() => {
                                                generateTopicMarkdown();
                                                showMoreOptionsMenu = false;
                                            }}
                                        >
                                            <div
                                                class="topic-header__option-button"
                                            >
                                                <SVGIcon
                                                    name="clipboard-document-list"
                                                    size="20"
                                                />
                                            </div>
                                            <div>Copy Snippets</div>
                                        </div>

                                        <div
                                            class="topic-header__dropdown-option"
                                            use:clickAction={() => {
                                                downloadTopicAsCSV();
                                                showMoreOptionsMenu = false;
                                            }}
                                        >
                                            <div
                                                class="topic-header__option-button"
                                            >
                                                <SVGIcon
                                                    name="csv-document"
                                                    size="20"
                                                />
                                            </div>
                                            <div>Download CSV</div>
                                        </div>

                                        {#if $ActiveTopic !== null && $ActiveTopic.snippets.length !== 0}
                                            <!-- No print for empty topics -->
                                            <div
                                                class="topic-header__dropdown-option"
                                                use:clickAction={() => {
                                                    openPrintDialog();
                                                    showMoreOptionsMenu = false;
                                                }}
                                            >
                                                <div
                                                    class="topic-header__option-button"
                                                >
                                                    <SVGIcon
                                                        name="printer"
                                                        size="20"
                                                    />
                                                </div>
                                                <div>Print</div>
                                            </div>
                                        {/if}

                                        <div
                                            class="topic-header__dropdown-option"
                                            use:clickAction={() => {
                                                AppStore.togglePanel(
                                                    Panels.SNIPPET_ARCHIVE,
                                                );
                                                showMoreOptionsMenu = false;
                                            }}
                                        >
                                            <div
                                                class="topic-header__option-button"
                                            >
                                                <SVGIcon
                                                    name="wallet"
                                                    size="20"
                                                />
                                            </div>
                                            <div>Completed</div>
                                        </div>

                                        <div
                                            class="topic-header__dropdown-option"
                                            use:clickAction={() => {
                                                AppStore.togglePanel(
                                                    Panels.ACTIVITY,
                                                );
                                                showMoreOptionsMenu = false;
                                            }}
                                        >
                                            <div
                                                class="topic-header__option-button"
                                            >
                                                <SVGIcon
                                                    name="activities"
                                                    size="20"
                                                />
                                            </div>
                                            <div>Activity</div>
                                        </div>
                                    </div>
                                </div>
                            {/if}
                        {/if}
                    </div>
                </div>
            {/if}
        </div>

        <DashboardViewManager
            {userTopicRole}
            {selectedTagNames}
            user={$TokenStore.user}
        />

        <SidePanel show={$AppStore.panel === Panels.SHARE}>
            <SharePreferencesPanel
                topic={$ActiveTopic}
                user={$TokenStore.user}
                showDashboardButton={false}
                showShareButton={false}
                on:close={AppStore.closePanels}
            />
        </SidePanel>

        <SidePanel show={$AppStore.panel === Panels.ACTIVITY}>
            <ActivitiesPanel
                topic={$ActiveTopic}
                open={$AppStore.panel === Panels.ACTIVITY}
                on:close={AppStore.closePanels}
            />
        </SidePanel>

        <SidePanel show={$AppStore.panel === Panels.SNIPPET_ARCHIVE}>
            <ArchivedSnippetsPanel />
        </SidePanel>
    </div>
{/key}

<style>
    .topic-page-wrapper {
        @apply flex w-full flex-col overflow-hidden;
    }

    .topic-header {
        @apply z-40 flex items-center justify-between gap-1 bg-white p-5 md:p-[20px_20px_4px_20px];
    }

    .topic-header__info {
        @apply flex flex-1 flex-col;
    }

    .topic-header__info-input {
        @apply bg-white;
    }

    .topic-header__info-footer {
        @apply flex w-fit flex-row items-center gap-1 text-xs;
    }

    .topic-header__option-wrapper {
        @apply flex flex-wrap items-center justify-between;
    }

    .topic-header__dropdown {
        @apply absolute right-0 z-[60] min-w-max rounded-md border border-solid border-gray-200 bg-white text-sm shadow-xl;
    }

    .topic-header__dropdown-options-wrapper {
        @apply p-1;
    }

    .topic-header__dropdown-option {
        @apply flex cursor-pointer select-none items-center gap-1 overflow-hidden rounded pl-2 pr-4 hover:bg-gray-200;
    }

    .topic-header__dropdown-option--disabled {
        @apply pointer-events-none cursor-not-allowed bg-gray-100 text-gray-300;
    }

    .topic-header__dropdown-option--install-extension {
        @apply bg-gray-100;
    }

    .topic-header__option-button {
        @apply relative self-baseline rounded p-1 hover:bg-gray-200;
    }

    .topic-header__option-text {
        @apply flex w-full flex-row items-center gap-2;
    }

    .topic-header__option-text__upgrade {
        @apply ml-auto rounded-full bg-yellow-400 p-1 text-2xs font-bold leading-[0.75] text-black;
    }

    .topic-page-wrapper input {
        @apply w-full text-xl font-semibold outline-none;
    }

    .delete-topic-modal-wrapper {
        @apply fixed bottom-0 top-0 z-50 flex h-full w-full flex-col items-center justify-center bg-black bg-opacity-30;
    }

    .delete-topic-modal {
        @apply mx-2 -mt-10 flex flex-col items-center justify-center rounded-xl bg-white px-4 pb-2 pt-4 shadow-2xl;
    }

    .delete-topic-modal h1 {
        @apply mb-2 text-center text-2xl font-bold;
    }

    .delete-topic-modal p {
        @apply px-2 text-center text-gray-600;
    }

    .delete-topic-modal__actions {
        @apply mt-2 flex w-full flex-row items-center justify-end gap-2 py-2;
    }

    .delete-topic-modal__actions .btn {
        @apply cursor-pointer rounded-sm px-2 py-1 hover:bg-gray-200;
    }

    .delete-topic-modal__actions .btn--danger {
        @apply font-bold text-pink-500 hover:bg-pink-200;
    }

    .topic-label {
        @apply mr-0.5 mt-0.5 select-none rounded-full bg-gray-200 px-2 py-0.5 text-3xs font-bold uppercase leading-tight tracking-wide text-gray-500 hover:bg-gray-300 hover:text-gray-900 !important;
    }
</style>
